import type {
  SelectedTable,
  SelectedTables,
} from 'components/DeviceView/deviceViewTypes';
import { produce } from 'immer';
import {
  type ChangeEvent,
  type DragEvent,
  type FC,
  type KeyboardEvent,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useStableValue } from '@ipfabric/ui-components/hooks/useStableValue';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { dragTypes } from 'components/DeviceView/deviceViewConstants';
import { useLocale } from 'containers/App/LocaleContext';
import { canSort } from 'utils/dnd/canSort';

interface Props {
  onUpdateTables: (data: SelectedTables) => void;
  selectedTables: SelectedTables;
}

interface DragTreeItem {
  label: string;
  path: string;
  type: string;
  webEndpoint: string;
}

interface DragTableItem {
  index: number;
  type: string;
  webEndpoint: string;
}

interface ItemProps {
  index: number;
  label: string;
  onUpdateTables: (data: SelectedTables) => void;
  path: string;
  selectedTables: SelectedTables;
  webEndpoint: string;
}

const Item: FC<ItemProps> = (props) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  const [editItem, setEditItem] = useState<boolean>(false);
  const [itemName, setItemName] = useState<string>(props.label);
  const clickEventListeners = useStableValue(
    () => new ManagedDomEventListeners(),
  );

  const handleRemove = useStableCallback((e: MouseEvent) => {
    e.stopPropagation();
    props.onUpdateTables(
      produce(props.selectedTables, (draft) => {
        const foundIndex = props.selectedTables.findIndex(
          (table) => table.webEndpoint === props.webEndpoint,
        );
        if (foundIndex > -1) {
          draft.splice(foundIndex, 1);
        }
      }),
    );
  });

  const handleEdit = useStableCallback((e: MouseEvent) => {
    e.stopPropagation();
    setEditItem(true);
  });

  const handleSaveName = useStableCallback(() => {
    props.onUpdateTables(
      produce(props.selectedTables, (draft) => {
        const foundIndex = props.selectedTables.findIndex(
          (table) => table.webEndpoint === props.webEndpoint,
        );
        if (foundIndex > -1) {
          // Fixed noUncheckedIndexedAccess NIM-19408
          draft[foundIndex]!.label = itemName;
        }
      }),
    );
    setEditItem(false);
  });

  const handleNameChange = useStableCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setItemName(e.target.value);
    },
  );

  const handleKeyPress = useStableCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.which === 13) {
        handleSaveName();
      }
    },
  );

  const handleInputDragStart = (e: DragEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (!editItem) {
      // We need to prevent calling onClick from OptionsWindow.
      // When element is replaced by input, it disappeares from DOM.
      // OptionsWindow can't find closest element from element not in DOM and closes window.
      if (editRef.current && removeRef.current && itemRef.current) {
        clickEventListeners.add(
          [editRef.current, 'click', handleEdit, true],
          [itemRef.current, 'dblclick', handleRemove, true],
          [removeRef.current, 'click', handleRemove, true],
        );
      }
    } else {
      // input appears, icons disappear, remove listener
      clickEventListeners.remove();
    }

    return () => {
      clickEventListeners.remove();
    };
  }, [editItem]);

  const [, drag] = useDrag({
    item: {
      index: props.index,
      type: dragTypes.TABLE,
      webEndpoint: props.webEndpoint,
    },
  });

  const [, drop] = useDrop({
    accept: dragTypes.TABLE,
    hover(item: DragTableItem, monitor: DropTargetMonitor) {
      if (!itemRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      if (!canSort(itemRef.current, monitor, dragIndex, hoverIndex)) {
        return;
      }

      props.onUpdateTables(
        produce(props.selectedTables, (draft) => {
          // Fixed noUncheckedIndexedAccess NIM-19408
          const draggedTable = props.selectedTables[dragIndex]!;
          draft.splice(dragIndex, 1);
          draft.splice(hoverIndex, 0, draggedTable);
        }),
      );

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.

      // Example: https://github.com/react-dnd/react-dnd/blob/09838c9c203554bfe23c97f7af82b120f7f28be8/packages/documentation/examples-hooks/src/04-sortable/simple/Card.tsx
      item.index = hoverIndex;
    },
  });

  drag(drop(itemRef));

  return (
    <div
      className={classNames(
        'device-view-setup-tables__selected-tables-item',
        'flex',
        'flex-row',
        'flex-nowrap',
        'items-center',
        'cursor-move',
        'pl-lgu-3',
        'pt-lgu-2',
        'pb-lgu-2',
      )}
      ref={itemRef}
    >
      {editItem ? (
        <input
          className='form-control form-control-sm'
          value={itemName}
          onKeyPress={handleKeyPress}
          onChange={handleNameChange}
          onBlur={handleSaveName}
          onDragStart={handleInputDragStart}
          draggable
        />
      ) : (
        <>
          <div className='flex flex-1 flex-row flex-nowrap items-center overflow-hidden'>
            <i className='fa fa-ellipsis-v mr-lgu-3' />
            <div className='flex flex-row flex-wrap overflow-hidden'>
              <span className='mr-lgu-3 truncate font-bold'>{props.label}</span>
              <span className='mr-lgu-3 whitespace-nowrap'>{props.path}</span>
            </div>
          </div>
          <div className='whitespace-nowrap'>
            <Button
              variant='tertiary'
              size='xxs'
              icon='fa-pen'
              ref={editRef}
              type='button'
            />
            <Button
              variant='tertiary'
              size='xxs'
              icon='fa-trash-can'
              className='mr-lgu-3'
              ref={removeRef}
              type='button'
            />
          </div>
        </>
      )}
    </div>
  );
};

const BareDeviceViewSetupTablesSelected: FC<Props> = (props) => {
  const msg = useLocale();

  const [{ isOver }, drop] = useDrop({
    accept: dragTypes.TREE,
    drop(item: DragTreeItem) {
      props.onUpdateTables(
        produce(props.selectedTables, (draft) => {
          draft.push({
            webEndpoint: item.webEndpoint,
            label: item.label,
            path: item.path,
          });
        }),
      );
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  // Hide horizontal scrollbar
  const renderTrackHorizontal = () => {
    return <div />;
  };

  return (
    <div
      className={classNames(
        'device-view-setup-tables__selected-tables-drop',
        isOver && 'device-view-setup-tables__selected-tables-drop--hover',
        'flex flex-1 flex-col overflow-hidden',
      )}
      ref={drop}
    >
      <p className='mb-lgu-2 mt-0 font-bold'>
        {msg.deviceView.setupTables.currentSettings}
      </p>
      <ScrollColumnView
        className='device-view-setup-tables__selected-tables-items'
        renderTrackHorizontal={renderTrackHorizontal}
      >
        <div
          className={classNames(
            'flex-1',
            !props.selectedTables.length && 'hidden',
          )}
        >
          {props.selectedTables.map((table: SelectedTable, index: number) => (
            <Item
              index={index}
              key={table.webEndpoint}
              label={table.label}
              onUpdateTables={props.onUpdateTables}
              path={table.path}
              selectedTables={props.selectedTables}
              webEndpoint={table.webEndpoint}
            />
          ))}
        </div>
      </ScrollColumnView>
    </div>
  );
};

export const DeviceViewSetupTablesSelected = memo(
  BareDeviceViewSetupTablesSelected,
);
